import React, { useState, useCallback, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import UpdateShimmer from "../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel.jsx";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import SupplierTabBar from "./supplierTabBar.js";


const UpdateSupplier = () => {
  const { sID } = useParams();
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const [supplierData, setSupplierData] = useState({});
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [originalEmail, setOriginalEmail] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);

  const fetchSupplierDetails = useCallback(async () => {
    if (!sID || !compID) {
      showErrorToast("Invalid supplier ID or company ID");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_SUPPLIER_BY_ID, {
        params: { CompID: compID, S_ID: sID },
      });

      const data = response.data.data || response.data;

      setSupplierData({
        ...data,
        S_ID: parseInt(sID),
        CompID: compID,
        S_DateTime: data.s_DateTime ? new Date(data.s_DateTime) : new Date(),
        S_Email: data.s_Email || "",
        S_Name: data.s_Name || "",
        S_Description: data.s_Description || "",
        S_Type: data.s_Type || "",
        S_Contact: data.s_Contact || "",
        S_Address1: data.s_Address1 || "",
        S_Address2: data.s_Address2 || "",
        Post_Code: data.post_Code || "",
        S_Country: data.s_Country || "",
        S_City: data.s_City ? String(data.s_City) : "",
        S_Note: data.s_Note || "",
        Is_Active: data.is_Active || true,
      });

      setSelectedCity(data.s_City ? String(data.s_City) : null);
      setOriginalEmail(data.s_Email || "");
    } catch (error) {
      showErrorToast("Failed to load supplier details");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [sID, compID]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setSupplierData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  // Handle toggle switch changes
  const handleToggleChange = useCallback((checked, name) => {
    setSupplierData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle dropdown and special input changes
  const handleSpecialInputChange = useCallback((name, value) => {
    setSupplierData((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  }, []);

  const submitSupplier = async (submitData) => {
    try {
      setLoading(true);
      submitData.CompID = compID;
      submitData.S_ID = parseInt(sID);

      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_SUPPLIER,
        submitData,
      );

      if (response.data.status === true) {

        showSuccessToast(
          response.data.message || "Supplier Successfully Updated."
        );
        navigate("/supplier/manageSupplier");
      } else {
        showErrorToast(response.data.message || "Error Updating Supplier");
      }

      return response.data;

    } catch (err) {
      if (err.response) {
        showErrorToast(
          err.response.data.message || "Error submitting supplier"
        );
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in supplier submission");
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!supplierData.S_Name.trim()) {
      showErrorToast("Please provide a valid supplier name");
      return;
    }

    if (supplierData.S_Country && !selectedCity) {
      showErrorToast("Please select a city for the chosen country");
      return;
    }

    const submitData = {
      ...supplierData,
      S_DateTime: new Date().toISOString(),
      S_City: selectedCity || "",
    };

    try {
      await submitSupplier(submitData);
    } catch (error) {
      // Error handled in submitSupplier
    }
  };

  // Email editing handlers
  const handleEditEmailStart = () => {
    setOriginalEmail(supplierData.S_Email);
    setIsEditingEmail(true);
  };

  const handleSaveEmail = async () => {
    if (!supplierData.S_Email || !validateEmail(supplierData.S_Email)) {
      showErrorToast("Please provide a valid email");
      setSupplierData((prev) => ({ ...prev, S_Email: originalEmail }));
      return;
    }

    setIsEditingEmail(false);

    try {
      setLoading(true);
      const requestBody = {
        S_ID: parseInt(sID),
        CompID: supplierData.CompID,
        S_Email: supplierData.S_Email,
        S_DateTime: supplierData.S_DateTime
          ? new Date(supplierData.S_DateTime)
          : new Date(),
      };

      const response = await axiosInstance.patch(
        APIs.PATCH_UPDATE_SUPPLIER_EMAIL_ADDRESS,
        requestBody
      );

      if (response.data.status) {
        showSuccessToast(response.data.message || "Email updated successfully");
      } else {
        showErrorToast(response.data.message || "Error updating email");
      }
      return response.data;
    } catch (err) {
      if (err.response) {
        showErrorToast(err.response.data.message || "Error updating email");
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in email submission");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEmail = () => {
    setSupplierData((prev) => ({ ...prev, S_Email: originalEmail }));
    setIsEditingEmail(false);
  };

  useEffect(() => {
    fetchSupplierDetails();
  }, [fetchSupplierDetails]);

  const RequiredLabel = ({ text }) => (
    <label className="form-label">
      {text} <span className="text-danger">*</span>
    </label>
  );

  const NormalLabel = ({ text }) => (
    <label className="form-label">{text}</label>
  );

  return (
    <>
      {loading && <Preloader />}
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/"><i className="fa fa-home me-1"></i>Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/supplier/manageSupplier"><i className="fa fa-building me-1"></i>Manage Suppliers</Link>
              </li>
              <li className="breadcrumb-item active"><i className="fa fa-edit me-1"></i>Update Supplier</li>
            </ol>
            <h1 className="page-header mb-0"><i className="fa fa-edit me-2 text-primary"></i>Update Supplier</h1>
          </div>
          <div className="ms-auto">
            <button
              className="btn px-4"
              onClick={handleSubmit}
              disabled={loading}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '20px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`,
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }
              }}
            >
              <i className="fa fa-save me-2"></i>
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>

        {sID && <SupplierTabBar supplierId={sID} />}

        <Panel className="card border-0 shadow-sm">
          <PanelHeader className="bg-primary text-white">Supplier Details</PanelHeader>
          <PanelBody className="p-4">
            {initialLoading ? (
              <UpdateShimmer />
            ) : (
              <>
                {/* Email Section */}
                <div className="mb-4 p-3 bg-light rounded shadow-sm border">
                  {isEditingEmail ? (
                    <div className="d-flex gap-2 align-items-end">
                      <FormInput
                        label="Email"
                        name="S_Email"
                        type="email"
                        value={supplierData.S_Email}
                        onchange={handleInputChange}
                        placeholder="Enter Email"
                        prepend={<i className="fa fa-envelope text-muted"></i>}
                      />
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleSaveEmail}
                      >
                        <i className="fa fa-check me-1"></i>Save
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleCancelEmail}
                      >
                        <i className="fa fa-times me-1"></i>Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2">
                      <h3 className="mb-0 text-primary">
                        <i className="fa fa-envelope me-2"></i>{supplierData.S_Email}
                      </h3>
                      <i
                        className="fa fa-pencil text-primary cursor-pointer"
                        onClick={handleEditEmailStart}
                      />
                    </div>
                  )}
                </div>

                <hr className="my-4" />

                {/* Supplier Information */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-building me-2"></i>Supplier Information
                </h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <RequiredLabel text="Supplier Name" />
                    <FormInput
                      name="S_Name"
                      value={supplierData.S_Name}
                      onchange={handleInputChange}
                      placeholder="Enter Supplier Name"
                      prepend={<i className="fa fa-building text-muted"></i>}
                    />
                  </div>
                  <div className="col-md-6">
                    <NormalLabel text="Supplier Type" />
                    <FormInput
                      name="S_Type"
                      value={supplierData.S_Type}
                      onchange={handleInputChange}
                      placeholder="Enter Supplier Type"
                      prepend={<i className="fa fa-tag text-muted"></i>}
                    />
                  </div>
                  <div className="col-md-12">
                    <NormalLabel text="Description" />
                    <FormInput
                      name="S_Description"
                      value={supplierData.S_Description}
                      onchange={handleInputChange}
                      placeholder="Enter Description"
                      prepend={<i className="fa fa-info text-muted"></i>}
                    />
                  </div>
                </div>

                <hr className="my-4" />

                {/* Contact Information */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-address-book me-2"></i>Contact Information
                </h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <NormalLabel text="Contact Number" />
                    <FormInput
                      name="S_Contact"
                      placeholder="Enter Contact Number"
                      type="text"
                      value={supplierData.S_Contact}
                      onchange={handleInputChange}
                      prepend={<i className="fa fa-phone text-muted"></i>}
                    />
                  </div>
                </div>

                <hr className="my-4" />

                {/* Address Information */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-map-marker me-2"></i>Address Information
                </h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-12">
                    <NormalLabel text="Address Line 1" />
                    <FormInput
                      name="S_Address1"
                      value={supplierData.S_Address1}
                      onchange={handleInputChange}
                      placeholder="Enter Address Line 1"
                      prepend={<i className="fa fa-road text-muted"></i>}
                    />
                  </div>
                  <div className="col-md-12">
                    <NormalLabel text="Address Line 2" />
                    <FormInput
                      name="S_Address2"
                      value={supplierData.S_Address2}
                      onchange={handleInputChange}
                      placeholder="Enter Address Line 2"
                      prepend={<i className="fa fa-road text-muted"></i>}
                    />
                  </div>
                </div>
                <div className="row mt-3">
                  <RequiredLabel text="Country / City" />
                  <CountryCitySelector
                    selectedCountry={supplierData.S_Country}
                    selectedCity={selectedCity}
                    setSelectedCity={setSelectedCity}
                    onChange={(data) =>
                      setSupplierData((prev) => ({ ...prev, ...data }))
                    }
                    setLoading={setLoading}
                    isDarkMode={isDarkMode}
                    selectedValueIsLabel={true}
                    countryFieldName="S_Country"
                    cityFieldName="S_City"
                  />
                </div>
                <div className="row g-3 mt-2 mb-4">
                  <div className="col-md-6">
                    <NormalLabel text="Post Code" />
                    <FormInput
                      name="Post_Code"
                      value={supplierData.Post_Code}
                      onchange={handleInputChange}
                      placeholder="Enter Post Code"
                      prepend={<i className="fa fa-map-pin text-muted"></i>}
                    />
                  </div>
                </div>

                <hr className="my-4" />

                {/* Additional Information */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-info-circle me-2"></i>Additional Information
                </h5>
                <div className="row g-3 mt-2 mb-4">
                  <div className="col-md-12">
                    <NormalLabel text="Notes" />
                    <FormInput
                      name="S_Note"
                      value={supplierData.S_Note}
                      onchange={handleInputChange}
                      placeholder="Enter Notes"
                      prepend={<i className="fa fa-sticky-note text-muted"></i>}
                    />
                  </div>
                </div>

                <hr className="my-4" />

                {/* Status */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-toggle-on me-2"></i>Status
                </h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <ToggleSwitch
                      isChecked={supplierData.Is_Active || false}
                      onToggle={(checked) => handleToggleChange(checked, "Is_Active")}
                      name="Is_Active"
                      label="Active"
                      themeColor={themeColor}
                    />
                  </div>
                </div>
              </>
            )}
          </PanelBody>
        </Panel>
      </div>
    </>
  );
};

export default UpdateSupplier;